// <powerlete-testimonials> — lean, library-free controller for the testimonial
// rail. The rail itself is a CSS scroll-snap row that works with touch/trackpad
// alone; this element only adds the OPTIONAL prev/next arrows, dots and autoplay
// when the merchant turns them on. Reduced-motion is respected (no smooth scroll,
// no autoplay). No external dependency (the live Gecko section used Swiper).
class PowerleteTestimonials extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-pw-tm-rail]');
    if (!this.rail) return;
    this.slides = Array.from(this.rail.children);
    this.prevBtn = this.querySelector('[data-pw-tm-prev]');
    this.nextBtn = this.querySelector('[data-pw-tm-next]');
    this.dotsWrap = this.querySelector('[data-pw-tm-dots]');
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));

    if (this.dotsWrap) this.buildDots();
    this.observe();

    if (this.hasAttribute('data-autoplay') && !this.reduce && this.slides.length > 1) {
      this.startAutoplay();
    }
  }

  cardStep() {
    if (this.slides.length < 2) return this.rail.clientWidth;
    return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
  }

  step(dir) {
    this.rail.scrollBy({ left: this.cardStep() * dir, behavior: this.reduce ? 'auto' : 'smooth' });
  }

  toSlide(i) {
    const target = this.slides[i];
    if (!target) return;
    this.rail.scrollTo({ left: target.offsetLeft - this.slides[0].offsetLeft, behavior: this.reduce ? 'auto' : 'smooth' });
  }

  buildDots() {
    this.dots = this.slides.map((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pw-tm__dot';
      // Localised dot label. Template comes from a data attr (set from a locale key in the section,
      // see Loop 18); `[n]` is replaced with the 1-based index. English fallback used if unset.
      const dotTemplate = this.dataset.dotLabel || 'Go to review [n]';
      b.setAttribute('aria-label', dotTemplate.replace('[n]', String(i + 1)));
      b.addEventListener('click', () => this.toSlide(i));
      this.dotsWrap.appendChild(b);
      return b;
    });
  }

  setActive(i) {
    this.dots?.forEach((d, di) => d.setAttribute('aria-current', di === i ? 'true' : 'false'));
  }

  observe() {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const i = this.slides.indexOf(e.target);
            if (i > -1) this.setActive(i);
          }
        });
      },
      { root: this.rail, threshold: 0.6 }
    );
    this.slides.forEach((s) => io.observe(s));
    // Store so disconnectedCallback can disconnect it; otherwise the observer (and the slides it
    // observes) leak when the element is removed, e.g. a theme-editor section morph.
    this._io = io;
  }

  startAutoplay() {
    const delay = (parseFloat(this.getAttribute('data-delay')) || 4) * 1000;
    let dir = 1;
    const tick = () => {
      const atEnd = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 4;
      const atStart = this.rail.scrollLeft <= 4;
      if (atEnd) dir = -1;
      else if (atStart) dir = 1;
      this.step(dir);
    };
    // start() clears any existing timer FIRST: mouseleave + focusout + visibilitychange can each
    // call start() while a timer is already live, and without this the intervals stacked and the
    // rail accelerated.
    const stop = () => window.clearInterval(this.timer);
    const start = () => {
      stop();
      this.timer = window.setInterval(tick, delay);
    };
    // AbortController so disconnectedCallback can drop every listener (incl. the document-level
    // visibilitychange one) when the element is removed, e.g. a theme-editor section morph.
    this._autoplayStop = stop;
    this._autoplayAbort = new AbortController();
    const opts = { signal: this._autoplayAbort.signal };
    this.addEventListener('mouseenter', stop, opts);
    this.addEventListener('mouseleave', start, opts);
    this.addEventListener('focusin', stop, opts);
    this.addEventListener('focusout', start, opts);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()), opts);
    // Safari doesn't focus a tapped <button>, so focusin/mouseenter alone miss
    // touch taps on the dots/arrows. pointerdown catches mouse and touch alike
    // and, once the visitor has taken control, stays stopped for good.
    this.addEventListener('pointerdown', stop, { ...opts, once: true });
    start();
  }

  disconnectedCallback() {
    // Stop the autoplay timer and remove all autoplay listeners (incl. document visibilitychange)
    // so neither leaks after the element is removed.
    if (this._autoplayStop) this._autoplayStop();
    if (this._autoplayAbort) this._autoplayAbort.abort();
    if (this._io) this._io.disconnect();
  }
}

if (!customElements.get('powerlete-testimonials')) {
  customElements.define('powerlete-testimonials', PowerleteTestimonials);
}
